import type { JSX } from 'react';
import { useMemo, useState } from 'react';

import { Plus } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface ScenarioCategoryFieldProps {
  value: string;
  categories: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  allowCreate?: boolean;
  className?: string;
}

const ScenarioCategoryField = ({
  value,
  categories,
  onChange,
  placeholder = 'Buscar o crear categoría…',
  ariaLabel = 'Categoría',
  allowCreate = true,
  className,
}: ScenarioCategoryFieldProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  const query = value.trim().toLowerCase();
  const filtered = useMemo(
    () => categories.filter((category) => category.toLowerCase().includes(query)),
    [categories, query]
  );
  const hasExactMatch = categories.some((category) => category.toLowerCase() === query);
  const showCreate = allowCreate && query.length > 0 && !hasExactMatch;

  const selectOption = (option: string): void => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div className={cn('relative', className)}>
      <Input
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        autoComplete="off"
      />
      {isOpen && (filtered.length > 0 || showCreate) ? (
        <div
          className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border-subtle
            bg-bg-surface shadow-md"
          role="listbox"
        >
          {filtered.map((category) => (
            <button
              key={category}
              type="button"
              role="option"
              aria-selected={category === value}
              className="block w-full px-3 py-2 text-left text-sm text-text-default hover:bg-bg-surface-alt"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectOption(category)}
            >
              {category}
            </button>
          ))}
          {showCreate ? (
            <button
              type="button"
              className="flex w-full items-center gap-2 border-t border-border-subtle px-3 py-2 text-left text-sm
                text-brand-vivid hover:bg-bg-surface-alt"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectOption(value.trim())}
            >
              <Plus className="size-4" aria-hidden />
              Crear categoría «{value.trim()}»
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ScenarioCategoryField;
